import { FC, useCallback, useState, useRef, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Title,
  Stack,
  Group,
  Button,
  LoadingOverlay,
  Alert,
  Modal,
  Textarea,
  Stepper,
} from '@mantine/core';
import {
  IconArrowLeft,
  IconDeviceFloppy,
  IconSend,
  IconAlertCircle,
  IconBell,
  IconInfoCircle,
} from '@tabler/icons-react';
import { Practitioner } from '@medplum/fhirtypes';
import { useTranslation } from 'react-i18next';
import { ReportEditor, ReportEditorRef } from './ReportEditor';
import { SignatureSection } from './components/SignatureSection';
import { useReportEditor } from './hooks/useReportEditor';
import { useReportSave } from './hooks/useReportSave';
import { TiptapJSON, SignerEntry } from './types/template.types';
import { validateReport } from './utils/section-validation';
import { usePdfActions } from '../diagnostic-reports/hooks/usePdfActions';
import { exportReportToPdfHtml2Pdf } from './services/pdf-export-html2pdf.service';
import { useReviewStatus } from '../diagnostic-reports/hooks/useReviewStatus';
import { useCommunications } from '../diagnostic-reports/hooks/useCommunications';
import { useCommentSubmission } from '../diagnostic-reports/hooks/useCommentSubmission';
import { useReviewActions } from '../diagnostic-reports/hooks/useReviewActions';
import {
  ReviewStatusCard,
  CommunicationsCard,
  ReviewCommentsCard,
} from '../diagnostic-reports/components';
import { ROUTES } from '@consts/routes.consts';
import styles from './ReportEditorPage.module.css';
import { resolveId } from '@medplum/core';
import { useMedplum, useMedplumProfile } from '@medplum/react';
import { useDiagnosticReport } from '../diagnostic-reports/hooks/useDiagnosticReport';
import { useDisclosure } from '@mantine/hooks';
import { ReviewerSelectModal } from '../diagnostic-reports/components/ReviewerSelectModal';

export const ReportEditorPage: FC = () => {
  const { orderId, reportId } = useParams<{
    orderId: string;
    reportId?: string;
  }>();
  const medplum = useMedplum();
  const navigate = useNavigate();
  const { t } = useTranslation(['lims', 'common']);
  const currentUser = useMedplumProfile() as Practitioner;

  //  ── Diagnostic Report state ───────────────────────────────────────────────
  const { diagnosticReport, setDiagnosticReport } = useDiagnosticReport(
    reportId,
    orderId
  );

  // ── Editor orchestration ───────────────────────────────────────────────────
  const {
    loading: editorLoading,
    error: editorError,
    template,
    bindingData,
    savedContent,
    isReady,
    orderDetails,
  } = useReportEditor({
    orderId,
    report: diagnosticReport,
  });

  // ── Content state ──────────────────────────────────────────────────────────
  const [editorContent, setEditorContent] = useState<TiptapJSON | undefined>(
    undefined
  );
  const [signers, setSigners] = useState<SignerEntry[]>([
    { id: 'signer-0', name: '', title: '' },
    { id: 'signer-1', name: '', title: '' },
  ]);
  const [isEditable, setIsEditable] = useState<boolean>(true);

  // ── Refs ───────────────────────────────────────────────────────────────────
  const editorRef = useRef<ReportEditorRef>(null);

  // ── Reviewer selection state ───────────────────────────────────────────────
  const [opened, { open, close }] = useDisclosure(false);

  // ── Save ───────────────────────────────────────────────────────────────────
  const {
    saveDraft,
    submitForReview,
    loadReportContent,
    saving,
    error: saveError,
  } = useReportSave({
    serviceRequest: orderDetails.serviceRequest,
    patient: orderDetails.patient,
    specimens: orderDetails.specimens,
    templateId: 'neurocutaneous-syndromes',
    templateVersion: template?.version || '1.0.0',
    onSaveSuccess: (report) => {
      setDiagnosticReport(report);
      // If this was a new report (no reportId in URL yet), update the URL so
      // subsequent actions (e.g. Submit for Review) use the correct report ID.
      if (!reportId && report.id && orderId) {
        navigate(ROUTES.LIMS.EDIT_REPORT_EXISTING(orderId, report.id), {
          replace: true,
        });
      }
    },
  });

  // ── Review status ──────────────────────────────────────────────────────────
  const {
    reviewStatusData,
    currentUserReviewStatus,
    setCurrentUserReviewStatus,
    refetch: refetchReviewStatus,
  } = useReviewStatus(reportId, currentUser);

  // ── PDF ────────────────────────────────────────────────────────────────────
  const { pdfLoading, handlePreviewPDF, handleExportPDF, handlePrint } =
    usePdfActions({
      editorRef,
      template,
      content: isEditable ? editorContent : savedContent,
      orderId,
      signers: isEditable ? signers : undefined,
      bindingData,
    });

  // ── Communications ─────────────────────────────────────────────────────────
  const { communications, communicationsLoading, loadCommunications } =
    useCommunications(reportId);

  // ── Review actions (notify reviewers) ─────────────────────────────────────
  const {
    notifyModalOpen,
    notifyMessage,
    notifying,
    setNotifyMessage,
    openNotifyModal,
    closeNotifyModal,
    sendNotification,
  } = useReviewActions({
    diagnosticReport:
      diagnosticReport ?? ({ resourceType: 'DiagnosticReport' } as any),
    onNotificationSent: loadCommunications,
  });

  // ── PDF blob generation (used when report is finalized) ───────────────────
  const generatePdfBlob = useCallback(async (): Promise<Blob | null> => {
    const editorElement = editorRef.current?.getEditorElement();
    const content = isEditable ? editorContent : savedContent;
    if (!editorElement || !template || !content) {
      return null;
    }

    return exportReportToPdfHtml2Pdf(editorElement, {
      content,
      template,
      signers: isEditable ? signers : undefined,
      includePageNumbers: true,
      bindingData,
    });
  }, [
    editorRef,
    template,
    editorContent,
    savedContent,
    isEditable,
    signers,
    bindingData,
  ]);

  // ── Comment submission ─────────────────────────────────────────────────────
  const {
    submittingComment,
    reviewStatus,
    setReviewStatus,
    reviewComment,
    setReviewComment,
    submitComment,
  } = useCommentSubmission({
    reportId,
    diagnosticReport,
    currentUser,
    patient: orderDetails.patient ?? undefined,
    serviceRequest: orderDetails.serviceRequest ?? undefined,
    onCommentSubmitted: loadCommunications,
    onReviewStatusChange: setCurrentUserReviewStatus,
    onDiagnosticReportUpdate: setDiagnosticReport,
    generatePdfBlob,
  });

  // ── Step derived from report status ───────────────────────────────────────
  const activeStep =
    diagnosticReport?.status === 'final'
      ? 2
      : diagnosticReport?.status === 'preliminary'
        ? 1
        : 0;

  // ── Effects ───────────────────────────────────────────────────────────────
  // Load signers when editing an existing report
  useEffect(() => {
    if (!diagnosticReport?.id) {
      return;
    }
    loadReportContent(diagnosticReport.id).then((result) => {
      if (result?.signers?.length) {
        setSigners(result.signers);
      }
    });

    setIsEditable(
      currentUser.id === resolveId(diagnosticReport?.meta?.author) ||
        !diagnosticReport
    );

    refetchReviewStatus();
  }, [
    diagnosticReport,
    currentUser,
    loadReportContent,
    refetchReviewStatus,
    medplum,
  ]);

  // Load communications once we enter review / approved mode
  useEffect(() => {
    if (reportId && activeStep >= 1) {
      loadCommunications();
    }
  }, [reportId, activeStep, loadCommunications]);

  // Once the current user has already approved, default the action back to 'comment'
  useEffect(() => {
    if (currentUserReviewStatus === 'approved') {
      setReviewStatus('comment');
    }
  }, [currentUserReviewStatus, setReviewStatus]);

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleContentChange = useCallback((content: TiptapJSON) => {
    setEditorContent(content);
  }, []);

  const handleSignersChange = useCallback((newSigners: SignerEntry[]) => {
    setSigners(newSigners);
  }, []);

  const handleSaveDraft = useCallback(async () => {
    if (!editorContent) {
      return;
    }
    await saveDraft(editorContent, reportId, { signers });
  }, [editorContent, signers, reportId, saveDraft]);

  const handleSubmitForReview = useCallback(
    async (reviewerIds: string[]) => {
      if (!editorContent || !template) {
        return;
      }
      const validation = validateReport(template, editorContent);
      if (!validation.isValid) {
        return;
      }
      await submitForReview(editorContent, reviewerIds, reportId, { signers });
    },
    [editorContent, template, reportId, signers, submitForReview]
  );

  const handleNotifyReviewers = useCallback(async () => {
    if (editorContent) {
      await saveDraft(editorContent, reportId, { signers });
    }
    openNotifyModal();
  }, [editorContent, reportId, signers, saveDraft, openNotifyModal]);

  const handleSubmitComment = useCallback(async () => {
    await submitComment();
    await refetchReviewStatus();
  }, [submitComment, refetchReviewStatus]);

  const handleBack = useCallback(() => {
    if (orderId) {
      navigate(ROUTES.LIMS.ORDER_DETAILS(orderId));
    } else {
      navigate(ROUTES.LIMS.ROOT);
    }
  }, [navigate, orderId]);

  // ── Loading / error guards ─────────────────────────────────────────────────
  if (editorLoading) {
    return (
      <Box p="xl">
        <Paper p="xl" pos="relative" mih={400}>
          <LoadingOverlay visible />
        </Paper>
      </Box>
    );
  }

  if (editorError) {
    return (
      <Box p="xl">
        <Alert
          icon={<IconAlertCircle size={16} />}
          title={t('reportEditor.error', { ns: 'lims' })}
          color="red"
        >
          {editorError}
        </Alert>
        <Button
          mt="md"
          leftSection={<IconArrowLeft size={16} />}
          onClick={handleBack}
        >
          {t('buttons.back', { ns: 'common' })}
        </Button>
      </Box>
    );
  }

  if (!isReady || !template) {
    return (
      <Box p="xl">
        <Alert
          icon={<IconAlertCircle size={16} />}
          title={t('reportEditor.dataNotReady', { ns: 'lims' })}
          color="yellow"
        >
          {t('reportEditor.waitingForData', { ns: 'lims' })}
        </Alert>
      </Box>
    );
  }

  const validationResults =
    template && editorContent ? validateReport(template, editorContent) : null;

  // ── Editor column (left in review/approved, full-width in draft) ───────────
  const editorColumn = (
    <Stack gap="md">
      <ReportEditor
        ref={editorRef}
        template={template}
        initialContent={savedContent}
        bindingData={bindingData}
        onContentChange={isEditable ? handleContentChange : undefined}
        loading={editorLoading}
        onPreviewPDF={handlePreviewPDF}
        onExportPDF={handleExportPDF}
        onPrint={handlePrint}
        pdfLoading={pdfLoading}
        readOnly={!isEditable}
      />
      {isEditable && (
        <SignatureSection signers={signers} onChange={handleSignersChange} />
      )}
    </Stack>
  );

  // ── Review panel (right column in review/approved) ─────────────────────────
  const reviewPanel = diagnosticReport ? (
    <Stack gap="md">
      <ReviewStatusCard
        diagnosticReport={diagnosticReport}
        reviewStatusData={reviewStatusData}
        {...(isEditable && {
          serviceRequest: orderDetails.serviceRequest ?? undefined,
          patient: orderDetails.patient ?? undefined,
          onReviewersChanged: refetchReviewStatus,
          onDiagnosticReportUpdated: setDiagnosticReport,
        })}
      />
      <CommunicationsCard
        communications={communications}
        communicationsLoading={communicationsLoading}
      />
      <ReviewCommentsCard
        diagnosticReport={diagnosticReport}
        reviewStatusData={reviewStatusData}
        currentUserReviewStatus={currentUserReviewStatus}
        reviewStatus={reviewStatus}
        setReviewStatus={setReviewStatus}
        reviewComment={reviewComment}
        setReviewComment={setReviewComment}
        submittingComment={submittingComment}
        onSubmitComment={handleSubmitComment}
      />
    </Stack>
  ) : (
    <Alert
      variant="light"
      color="blue"
      title="No report available"
      icon={<IconInfoCircle />}
    >
      {'Something has went wrong. No diagnostic Report has been created. :('}
    </Alert>
  );

  return (
    <Box className={styles.pageWrapper}>
      {/* ── Sticky header ──────────────────────────────────────────────────── */}
      <Paper p="md" withBorder className={styles.stickyHeader}>
        <Group justify="space-between" wrap="wrap">
          <Group gap="md">
            <Button
              variant="subtle"
              leftSection={<IconArrowLeft size={16} />}
              onClick={handleBack}
            >
              {t('buttons.back', { ns: 'common' })}
            </Button>
            <Title order={2}>{t('reportEditor.title', { ns: 'lims' })}</Title>
          </Group>

          <Group gap="sm">
            {isEditable && (
              <Button
                variant="outline"
                leftSection={<IconDeviceFloppy size={16} />}
                onClick={handleSaveDraft}
                loading={saving}
                disabled={!editorContent}
              >
                {t('save', { ns: 'common' })}
              </Button>
            )}
            {isEditable && activeStep === 0 && (
              <Button
                variant="light"
                leftSection={<IconSend size={16} />}
                onClick={open}
                loading={saving}
                disabled={!editorContent || !validationResults?.isValid}
              >
                {t('reportEditor.submitForReview', { ns: 'lims' })}
              </Button>
            )}
            {isEditable && activeStep === 1 && (
              <Button
                variant="light"
                color="orange"
                leftSection={<IconBell size={16} />}
                onClick={handleNotifyReviewers}
                loading={saving}
                disabled={!editorContent}
              >
                {t('reportEditor.notifyReviewers', { ns: 'lims' })}
              </Button>
            )}
          </Group>
        </Group>
      </Paper>

      {/* ── Page content (scrollable) ───────────────────────────────────────── */}
      <Box p="md">
        <Stack gap="md">
          {/* Stepper */}
          <Paper p="md" withBorder>
            <Stepper active={activeStep} size="sm">
              <Stepper.Step
                label={t('reportEditor.steps.inProgress', { ns: 'lims' })}
              />
              <Stepper.Step
                label={t('reportEditor.steps.inReview', { ns: 'lims' })}
              />
              <Stepper.Step
                label={t('reportEditor.steps.approved', { ns: 'lims' })}
              />
            </Stepper>
          </Paper>

          {/* Save error */}
          {saveError && (
            <Alert icon={<IconAlertCircle size={16} />} color="red">
              {saveError}
            </Alert>
          )}

          {/* ── Step 0: In Progress ── full-width editor + submit for review ──────── */}
          {activeStep === 0 && editorColumn}

          {/* ── Step 1 / 2: In Review / Approved ── 2fr/1fr two-column ──────── */}
          {activeStep >= 1 && (
            <div className={styles.contentGrid}>
              {/* Left (2fr): editor */}
              {editorColumn}

              {/* Right (1fr): review panel */}
              {reviewPanel}
            </div>
          )}
        </Stack>
      </Box>

      {/* Notify reviewers modal */}
      <Modal
        opened={notifyModalOpen}
        onClose={closeNotifyModal}
        title={t('reportEditor.notifyReviewers', { ns: 'lims' })}
        size="md"
      >
        <Stack gap="md">
          <Textarea
            label={t('reportEditor.notifyMessage', { ns: 'lims' })}
            value={notifyMessage}
            onChange={(e) => setNotifyMessage(e.currentTarget.value)}
            minRows={4}
            autosize
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={closeNotifyModal}>
              {t('buttons.cancel', { ns: 'common' })}
            </Button>
            <Button
              leftSection={<IconSend size={16} />}
              onClick={sendNotification}
              loading={notifying}
              disabled={!notifyMessage.trim()}
            >
              {t('reportEditor.send', { ns: 'lims' })}
            </Button>
          </Group>
        </Stack>
      </Modal>

      <ReviewerSelectModal
        opened={opened}
        onClose={close}
        onAddReviewers={handleSubmitForReview}
      />
    </Box>
  );
};

export default ReportEditorPage;
